<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminAlertInsuranceTypeList"
        label="Alert Insurance Types"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Alert Insurance Types</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} Insurance Types</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search Insurance Types">
            <b-input
              v-model="search"
              placeholder="Search by name..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="insuranceTypes"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="name"
          default-sort-direction="asc"
        >
          <b-table-column field="name" label="Name" v-slot="props" sortable>
            <span class="has-text-weight-semibold">{{ props.row.name }}</span>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No insurance Types found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createInsuranceType">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create New InsuranceType</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field
                label="Name"
                required
                :type="{ 'is-danger': !!createErrors.name }"
                :message="createErrors.name"
              >
                <b-input
                  v-model="createForm.name"
                  placeholder="Enter insurance type name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdateInsuranceType">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit Insurance Type</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field label="Name" :type="{ 'is-danger': !!errors.name }" :message="errors.name">
                <b-input
                  v-model="editForm.name"
                  placeholder="Enter insurance type name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';
export default {
  name: 'AdminAlertInsuranceTypeList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      insuranceTypes: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        name: '',
      },
      createErrors: {},
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingInsuranceType: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadInsuranceTypes();
  },
  methods: {
    async loadInsuranceTypes() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('term', this.search.trim());
        const response = await request.get(`/admin/alerts/insurance-types?${params.toString()}`);
        this.insuranceTypes = response.data.insurance_types || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading insurance types:', error);
        this.$buefy.toast.open({
          message: 'Failed to load insurance types',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadInsuranceTypes();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadInsuranceTypes();
      }, 300);
    },
    openCreateModal() {
      this.createForm = {
        name: '',
      };
      this.createErrors = {};
      this.isCreateModalActive = true;
    },
    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        name: '',
      };
      this.createErrors = {};
    },
    async createInsuranceType() {
      this.isCreating = true;
      this.createErrors = {};
      try {
        const payload = {
          name: this.createForm.name?.trim(),
        };
        await request.post('/admin/alerts/insurance-types', payload);
        this.$buefy.toast.open({
          message: 'InsuranceType created successfully',
          type: 'is-success',
        });
        this.closeCreateModal();
        this.loadInsuranceTypes();
      } catch (error) {
        console.error('Error creating insurance type:', error);
        if (error.response?.data?.errors) {
          this.createErrors = error.response.data.errors;
        } else {
          const msg = error.response?.data?.code == 422 ? 'InsuranceType already exists' : 'Failed to create insurance type';
          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        }
      } finally {
        this.isCreating = false;
      }
    },
    openEditModal(obj) {
      this.editingInsuranceType = obj;
      this.editForm = {
        name: obj.name,
      };
      this.errors = {};
      this.showEditModal = true;
    },
    async handleUpdateInsuranceType() {
      this.editLoading = true;
      this.errors = {};
      try {
        const payload = {
          name: this.editForm.name?.trim(),
        };
        await request.put(`/admin/alerts/insurance-types/${this.editingInsuranceType.id}`, payload);
        this.$buefy.toast.open({
          message: 'InsuranceType updated successfully',
          type: 'is-success',
        });
        this.showEditModal = false;
        this.loadInsuranceTypes();
      } catch (error) {
        console.error('Error updating insurance type:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          let msg = error.response?.data?.code == 422 ? 'InsuranceType already exists' : 'Failed to update insurance type';

          this.$buefy.toast.open({
            message: msg,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update insurance type',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },
    confirmDelete(obj) {
      this.$buefy.dialog.confirm({
        title: 'Delete InsuranceType',
        message: `Are you sure you want to delete "<strong>${obj.name}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteInsuranceType(obj),
      });
    },
    async deleteInsuranceType(obj) {
      try {
        await request.delete(`/admin/alerts/insurance-types/${obj.id}`);
        this.$buefy.toast.open({
          message: 'Insurance type deleted successfully',
          type: 'is-success',
        });
        this.loadInsuranceTypes();
      } catch (error) {
        console.error('Error deleting insurance type:', error);
        let errorMessage = 'Failed to delete insurance type';
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this insurance type - it may still be in use by alerts';
        }
        // Show error message in a dialog for better visibility
        this.$buefy.dialog.alert({
          title: 'Delete Failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>
